import { Button, Flex, Image, styled, Text } from '@shared/components';
import { type UseFormReturn } from 'react-hook-form';

import {
  AlternativeForeignerRegistrationForm,
  type AlternativeForeignerRegistrationFormProps,
  AlternativeRegistrationErrorState,
  type AlternativeRegistrationErrorStateType,
} from '../../components';
import {
  type AlternativeForeignerRegistrationFormOutput,
  type AlternativeForeignerRegistrationFormInput,
} from '../../schemas/alternative-foreigner-registration-schema';
import { type AlternativeRegistrationModalLogicState } from '../alternative-registration-modal-logic/alternative-registration-modal-logic';

import { getCountryCodeOptions, getEnglishNameTitleOptions } from './utils';

const StyledNoCameraPermissionOverlayWrapper = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.shape.rounded(2),
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: theme.spacing(4),
  width: '100%',
  height: '658px',
}));

interface AlternativeForeignerRegistrationModalLogicContentProps {
  state: AlternativeRegistrationModalLogicState;
  form: UseFormReturn<
    AlternativeForeignerRegistrationFormInput,
    unknown,
    AlternativeForeignerRegistrationFormOutput
  >;
  cameraReady: boolean;
  slotProps?: {
    documentImageField?: {
      onClick?: AlternativeForeignerRegistrationFormProps['slotProps']['documentImageField']['onClick'];
    };
    error?: AlternativeRegistrationErrorStateType;
  };
  onRetryCamera?: () => void;
  imageData?: string;
}

export function AlternativeForeignerRegistrationModalLogicContent(
  props: Readonly<AlternativeForeignerRegistrationModalLogicContentProps>,
) {
  const { state, form, cameraReady: isCameraReady, slotProps, onRetryCamera, imageData } = props;

  switch (state) {
    case 'form':
      return (
        <AlternativeForeignerRegistrationForm
          form={form}
          slotProps={{
            nameTitleField: {
              name: 'nameTitle',
              options: getEnglishNameTitleOptions(),
            },
            firstNameField: {
              name: 'firstName',
            },
            middleNameField: {
              name: 'middleName',
            },
            lastNameField: {
              name: 'lastName',
            },
            documentTypeField: {
              name: 'documentType',
            },
            countryCodeField: {
              name: 'countryCode',
              options: getCountryCodeOptions(),
            },
            passportNumberField: {
              name: 'passportNumber',
            },
            documentImageField: {
              name: 'documentImage',
              onClick: slotProps?.documentImageField?.onClick,
            },
            causeField: {
              name: 'cause',
            },
          }}
        />
      );
    case 'image-capturing':
      return !isCameraReady ? (
        <StyledNoCameraPermissionOverlayWrapper>
          <Flex
            sx={{
              flexDirection: 'column',
              gap: (theme) => theme.spacing(2),
              alignItems: 'center',
            }}
          >
            <Text variant='h3' sx={{ color: (theme) => theme.palette.background.paper }}>
              ไม่สามารถเปิดกล้องได้
            </Text>
            <Text sx={{ color: (theme) => theme.palette.background.paper }}>
              โปรดอนุญาตให้เว็บไซต์เข้าถึงกล้องในเบราว์เซอร์ และลองอีกครั้ง
            </Text>
          </Flex>
          <Button size='large' onClick={onRetryCamera}>
            ลองอีกครั้ง
          </Button>
        </StyledNoCameraPermissionOverlayWrapper>
      ) : null;
    case 'image-confirmation':
      return (
        <Image
          src={imageData ?? ''}
          alt='Captured frame'
          sx={(theme) => ({
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            borderRadius: theme.shape.rounded(2),
            [theme.breakpoints.down('tablet')]: {
              borderRadius: theme.shape.rounded(0),
            },
          })}
        />
      );
    case 'error':
      return <AlternativeRegistrationErrorState type={slotProps?.error ?? 'user-blocked'} />;
    default:
      return null;
  }
}
